import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AssessmentTopic } from '../../interfaces/career-setup/assessment-topic.interface';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { PageResponse } from '../../interfaces/page-response.interface';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { AssessmentTopicService } from './assessment-topic.service';

@Injectable({
  providedIn: 'root'
})
export class AssessmentTopicApiService extends AssessmentTopicService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/assessment-topic`;

  constructor() {
    super();
  }

  override findAll(params: CommonSearchParam): Observable<PageResponse<AssessmentTopic>> {
    const httpParams = convertToHttpParams(params);
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<AssessmentTopic>>>(this.baseUrl, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 25, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string): Observable<AssessmentTopic> {
    return this.wrapRequest(
      this.http.get<ApiResponse<AssessmentTopic>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<AssessmentTopic>): Observable<AssessmentTopic> {
    return this.wrapRequest(
      this.http.post<ApiResponse<AssessmentTopic>>(this.baseUrl, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<AssessmentTopic>): Observable<AssessmentTopic> {
    return this.wrapRequest(
      this.http.put<ApiResponse<AssessmentTopic>>(`${this.baseUrl}/${id}`, data, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean): Observable<AssessmentTopic> {
    return this.wrapRequest(
      this.http.patch<ApiResponse<AssessmentTopic>>(`${this.baseUrl}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string): Observable<void> {
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }
}
